import React, {
	createContext,
	useCallback,
	useContext,
	useMemo,
	useState
} from 'react';
import {
	ALL_OPTION_VALUE,
	EMPTY_FILTER_SELECTION,
	FILTER_DEFINITIONS
} from '../TrainingReports.constants';
import {
	FilterKey,
	FilterRow,
	FilterSelection
} from '../TrainingReports.types';

interface ReportFiltersContextValue {
	selection: FilterSelection;
	search: string;
	setSearch: (search: string) => void;
	toggleValue: (key: FilterKey, value: string) => void;
	selectValue: (key: FilterKey, value: string) => void;
	clearFilter: (key: FilterKey) => void;
	clearAll: () => void;
	activeFilterCount: number;
	moreFiltersCount: number;
	hasActiveFilters: boolean;
}

const ReportFiltersContext = createContext<ReportFiltersContextValue | null>(
	null
);

const MORE_FILTER_KEYS = FILTER_DEFINITIONS.filter(
	(definition) => definition.row === FilterRow.More
).map((definition) => definition.key);

export const ReportFiltersProvider: React.FC = ({ children }) => {
	const [selection, setSelection] = useState<FilterSelection>(
		EMPTY_FILTER_SELECTION
	);
	const [search, setSearch] = useState('');

	const toggleValue = useCallback((key: FilterKey, value: string) => {
		setSelection((prev) => {
			const current = prev[key];
			return {
				...prev,
				[key]: current.includes(value)
					? current.filter((item) => item !== value)
					: [...current, value]
			};
		});
	}, []);

	const selectValue = useCallback((key: FilterKey, value: string) => {
		setSelection((prev) => ({
			...prev,
			[key]: value === ALL_OPTION_VALUE ? [] : [value]
		}));
	}, []);

	const clearFilter = useCallback((key: FilterKey) => {
		setSelection((prev) => ({ ...prev, [key]: [] }));
	}, []);

	const clearAll = useCallback(() => {
		setSelection(EMPTY_FILTER_SELECTION);
		setSearch('');
	}, []);

	const activeFilterCount = useMemo(() => {
		const selectedValues = Object.values(selection).reduce(
			(total, values) => total + values.length,
			0
		);
		return selectedValues + (search.trim() ? 1 : 0);
	}, [selection, search]);

	const moreFiltersCount = useMemo(
		() =>
			MORE_FILTER_KEYS.filter((key) => selection[key].length > 0).length,
		[selection]
	);

	const value = useMemo<ReportFiltersContextValue>(
		() => ({
			selection,
			search,
			setSearch,
			toggleValue,
			selectValue,
			clearFilter,
			clearAll,
			activeFilterCount,
			moreFiltersCount,
			hasActiveFilters: activeFilterCount > 0
		}),
		[
			selection,
			search,
			toggleValue,
			selectValue,
			clearFilter,
			clearAll,
			activeFilterCount,
			moreFiltersCount
		]
	);

	return (
		<ReportFiltersContext.Provider value={value}>
			{children}
		</ReportFiltersContext.Provider>
	);
};

export const useReportFilters = (): ReportFiltersContextValue => {
	const context = useContext(ReportFiltersContext);

	if (!context) {
		throw new Error(
			'useReportFilters must be used within a ReportFiltersProvider'
		);
	}

	return context;
};
